/**
 * src/hooks/useGeoLinkPendingTrades.ts
 *
 * Polls GeoLink's pending Zenex trade pipeline (`open` + `close`) at a
 * fixed interval and applies the logical-key dedup contract from §6 of the
 * integration guide:
 *
 *   - Dedup key      = server-populated `logical_key` (fall-back
 *     `${rule_id}_${matched_public_key}`). We read it via
 *     `tradeLogicalKey()` so the client contract matches the server.
 *   - Every poll response REPLACES the local map — do not merge on top of
 *     stale state — the `id` changes on every location update and would
 *     leave ghosts otherwise.
 *   - The "new-action" notification callback only fires when the logical
 *     key is absent from the previous snapshot.
 *
 * 24h TTL invariant (integration-guide §4.6):
 *   GeoLink filters pending rows where `received_at + 24h < now` out of
 *   both list endpoints and marks them `expired` on the per-id fetch.
 *   This is filter-on-read — the row is NOT deleted. After the TTL
 *   lapses, the engine re-emits a fresh pending with a new `received_at`
 *   on the next location tick, provided the rule's own
 *   `execution_time_window_seconds` is ≤ 24h (the UI default). If a
 *   rule is configured with a longer window, the list will hide the
 *   pending at 24h but dedup will refuse to emit a replacement until
 *   the window lapses — don't configure close rules with windows > 24h.
 *
 * The hook is split-aware: open and close lists are tracked separately
 * (different GeoLink endpoints), but the output is a single merged list so
 * the UI doesn't have to know about the split.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPendingOpens,
  getPendingCloses,
  tradeLogicalKey,
  type PendingTradeAction,
} from '../services/geoLinkTradeService';

export interface UseGeoLinkPendingTradesOptions {
  /** User's public key (G- or C-address). The public key is matched against
   *  GeoLink's `matched_public_key` index, so pass the same address the rule
   *  was registered against. */
  publicKey: string | null | undefined;
  /** Poll interval in ms. Defaults to 15s — match the deposits poll. */
  pollIntervalMs?: number;
  /** Fires ONCE per newly appearing logical key, across both lists. */
  onNewPendingTrade?: (trade: PendingTradeAction) => void;
  /** When false, the hook is idle (no polling, empty results). */
  enabled?: boolean;
  /**
   * How long (in ms) to suppress stale sibling actions after dismissing a
   * logical key via `dismissByLogicalKey`. Dedup keys on `(rule_id, user)`,
   * so if GeoLink has older pending actions for the same rule they would
   * otherwise "resurrect" once the newer one is settled. A short window
   * gives GeoLink time to reconcile its own backlog before we let any
   * ghosts through. Defaults to 120s; set to 0 to disable.
   */
  suppressionWindowMs?: number;
}

export interface UseGeoLinkPendingTradesResult {
  trades: PendingTradeAction[];
  opens:  PendingTradeAction[];
  closes: PendingTradeAction[];
  /** Count of pending actions whose `status === 'pending'`. */
  pendingCount: number;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  /** Force a refresh (returns the fresh snapshot). */
  refresh: () => Promise<PendingTradeAction[]>;
  /**
   * Optimistically drop a pending trade from the local snapshot by logical
   * key — use this right after calling `/complete` or `/cancel` so the UI
   * doesn't flash the stale row for one poll interval.
   */
  dismissByLogicalKey: (key: string) => void;
}

export function useGeoLinkPendingTrades({
  publicKey,
  pollIntervalMs = 15_000,
  onNewPendingTrade,
  enabled = true,
  suppressionWindowMs = 120_000,
}: UseGeoLinkPendingTradesOptions): UseGeoLinkPendingTradesResult {
  const [opens,  setOpens]  = useState<PendingTradeAction[]>([]);
  const [closes, setCloses] = useState<PendingTradeAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  // Track the previous logical-key set per list so onNewPendingTrade fires
  // exactly once per new action and doesn't double-fire on `id` churn.
  const prevOpenKeysRef  = useRef<Set<string>>(new Set());
  const prevCloseKeysRef = useRef<Set<string>>(new Set());
  const onNewRef = useRef(onNewPendingTrade);
  useEffect(() => { onNewRef.current = onNewPendingTrade; }, [onNewPendingTrade]);

  // Recently-dismissed logical keys (rule_id_publicKey -> settled-at ms).
  // See `dismissByLogicalKey` for the rationale. We prune expired entries on
  // every poll so the map can't grow unbounded across a long session.
  const recentlySettledRef = useRef<Map<string, number>>(new Map());

  const pollOnce = useCallback(async (): Promise<PendingTradeAction[]> => {
    if (!publicKey || !enabled) {
      setOpens([]); setCloses([]); setError(null); setLastFetchedAt(Date.now());
      return [];
    }
    setLoading(true);
    try {
      // Fire both fetches in parallel — each is independent.
      const [openList, closeList] = await Promise.all([
        getPendingOpens(publicKey).catch(err => {
          return [];
        }),
        getPendingCloses(publicKey).catch(err => {
          return [];
        }),
      ]);

      // Logical-key dedup (§6 of the spec) — collapse duplicates just in case
      // GeoLink returns a pair with the same (rule_id, matched_public_key)
      // across polls. We keep the entry with the newest received_at.
      const dedup = (list: PendingTradeAction[]): PendingTradeAction[] => {
        const byKey = new Map<string, PendingTradeAction>();
        for (const t of list) {
          const key = tradeLogicalKey(t);
          const existing = byKey.get(key);
          if (!existing) { byKey.set(key, t); continue; }
          const a = Date.parse(t.received_at || '') || 0;
          const b = Date.parse(existing.received_at || '') || 0;
          if (a >= b) byKey.set(key, t);
        }
        return Array.from(byKey.values());
      };

      // Prune expired suppression entries and filter out anything the user
      // just settled. This prevents "dedup resurrection": if GeoLink had
      // multiple pending actions for the same (rule_id, user) the dedup
      // would hide the older one; once the newer is settled, the older
      // would resurface on the next poll even though it's the same logical
      // intent the user already acted on.
      //
      // CRITICAL: suppression must only catch GHOSTS, not legitimate
      // re-triggers. Rules are static: the same (rule_id, user) pair fires
      // every time the user walks into the geofence, so a naive
      // "suppress everything with this key for N seconds" would hide the
      // very next legit close_position that comes in after the user opens
      // a new position. We disambiguate by `received_at`:
      //
      //   • ghost:       received_at <= dismissedAt  → suppress
      //   • new trigger: received_at >  dismissedAt  → show
      //
      // GeoLink server-timestamps `received_at`, so this is robust to
      // client clock skew as long as the two clocks aren't off by more
      // than the poll interval.
      const now = Date.now();
      if (suppressionWindowMs > 0) {
        // `for..of` on a Map requires es2015+ / downlevelIteration, which
        // CRA's default tsconfig doesn't enable. `forEach` works on every
        // target and lets us delete entries mid-iteration safely.
        recentlySettledRef.current.forEach((settledAt, k) => {
          if (now - settledAt > suppressionWindowMs) {
            recentlySettledRef.current.delete(k);
          }
        });
      }
      const isSuppressed = (t: PendingTradeAction) => {
        if (suppressionWindowMs <= 0) return false;
        const settledAt = recentlySettledRef.current.get(tradeLogicalKey(t));
        if (!settledAt) return false;
        if (now - settledAt > suppressionWindowMs) return false;
        const receivedAt = Date.parse(t.received_at || '') || 0;
        // New trigger after dismissal → let it through.
        return receivedAt <= settledAt;
      };

      const uniqueOpens  = dedup(openList).filter(t => !isSuppressed(t));
      const uniqueCloses = dedup(closeList).filter(t => !isSuppressed(t));

      // Fire onNewPendingTrade for entries that weren't in the previous snapshot.
      // Use logical keys so the "new" flag is stable across `id` churn.
      const openKeys  = new Set(uniqueOpens.map(tradeLogicalKey));
      const closeKeys = new Set(uniqueCloses.map(tradeLogicalKey));

      if (onNewRef.current) {
        for (const t of uniqueOpens) {
          const key = tradeLogicalKey(t);
          if (!prevOpenKeysRef.current.has(key) && t.status === 'pending') {
            onNewRef.current(t);
          }
        }
        for (const t of uniqueCloses) {
          const key = tradeLogicalKey(t);
          if (!prevCloseKeysRef.current.has(key) && t.status === 'pending') {
            onNewRef.current(t);
          }
        }
      }
      prevOpenKeysRef.current  = openKeys;
      prevCloseKeysRef.current = closeKeys;

      setOpens(uniqueOpens);
      setCloses(uniqueCloses);
      setError(null);
      setLastFetchedAt(Date.now());
      return [...uniqueOpens, ...uniqueCloses];
    } catch (err) {
      const msg = (err as Error)?.message || 'Failed to fetch pending trades';
      setError(msg);
      return [];
    } finally {
      setLoading(false);
    }
  }, [publicKey, enabled, suppressionWindowMs]);

  useEffect(() => {
    // Reset dedup state when the user (or enabled flag) changes so we
    // re-announce everything instead of silently hiding entries the new
    // user hasn't seen yet. The suppression window is also scoped to the
    // current user — a different user's settled actions shouldn't hide
    // this user's pending ones.
    prevOpenKeysRef.current  = new Set();
    prevCloseKeysRef.current = new Set();
    recentlySettledRef.current = new Map();
    setOpens([]); setCloses([]);
    if (!publicKey || !enabled) return;

    pollOnce();
    if (pollIntervalMs > 0) {
      const id = window.setInterval(pollOnce, pollIntervalMs);
      return () => window.clearInterval(id);
    }
  }, [publicKey, enabled, pollIntervalMs, pollOnce]);

  const dismissByLogicalKey = useCallback((key: string) => {
    // Record the dismissal time so subsequent polls suppress stale
    // siblings with the same (rule_id, user) for `suppressionWindowMs`.
    recentlySettledRef.current.set(key, Date.now());
    setOpens (prev => prev.filter(t => tradeLogicalKey(t) !== key));
    setCloses(prev => prev.filter(t => tradeLogicalKey(t) !== key));
  }, []);

  const trades = useMemo(() => [...opens, ...closes], [opens, closes]);
  const pendingCount = useMemo(
    () => trades.filter(t => t.status === 'pending').length,
    [trades],
  );

  return {
    trades,
    opens,
    closes,
    pendingCount,
    loading,
    error,
    lastFetchedAt,
    refresh: pollOnce,
    dismissByLogicalKey,
  };
}
